/**
 * SIP 明细表的**列定义** —— 唯一出处（v0.51）。
 *
 * ## 为什么要抽成文件
 *
 * 原来列是**散在三处**的：① `styles.css` 的 `.sip-c-*` 宽度类
 * ② `SipItemsTable.tsx` 的 `<th>` 硬编码 ③ 表头数组 `TEXT_COLS`。
 * 改一次列宽/加一列要动三个文件 ⇒ 本项目吃过「同一语义几份实现」的亏
 * （v0.50 的 `AA2` 修复就是加错了地方，产物一字节未变）。
 * ⇒ 现在**列的顺序、宽度、默认显隐、是否可关、提示文案**全部只在这里声明，
 *   `<colgroup>` / `<thead>` / `<tbody>` 三处都从这一个数组渲染。
 *
 * ## 🔴 持久化键为什么是 `.v1` 而不是沿用旧键
 *
 * 项目纪律：**默认值变了要换持久化键**。
 * 本轮**默认显示集合变了**（新收 3 列、且上下公差合并成一格），若沿用旧键，
 * 上次用过的浏览器会拿到**旧集合**（少列/多列），表现为「我明明设过却没生效」。
 * ⇒ 键名带版本，改默认值时**同步升版**。
 *
 * ## 宽度是**固定像素**，不是 `min-width`
 *
 * 原来用 `min-width` + 输入框 `width:100%` ⇒ 浏览器把剩余空间**均分**给
 * `TEXT_COLS` 全部 6 列，于是「上公差」（内容只有 `0.5`）和「检验标准」
 * 一样宽。实测 1440 桌面上表宽 1410 > 容器 1391 ⇒ 差 19px 也要横向滚动。
 * ⇒ 改用 `<colgroup>` + 固定 `width`，让**长内容列宽、窄内容列窄**。
 */
import { useState } from 'react';

export interface SipColDef {
  /** `SipRow` 的字段名；`tol` = 上下公差合并格；`_ops` = 行操作列。 */
  key: string;
  label: string;
  /** 固定像素宽（写进 `<colgroup>`）。 */
  w: number;
  /** 默认是否显示。 */
  defaultOn: boolean;
  /** 是否允许用户在「列设置」里关掉（核心列 = 不可关）。 */
  optional: boolean;
  /** 表头 `title` 提示（**不许带 markdown**，见护栏 `TestNoMarkdownInUserText`）。 */
  hint: string;
  /**
   * 条件必填（v0.75）：`'proj'` = 这一行的**检验项目填了**，这一格就不能空。
   *
   * 用户 2026-10-19 原话：「必填项的根据前面检验项目如果填写了那检验工具、
   * 检验频率为必填」。判据只写在这里，表头的 `*` 与单元格红框都从它渲染；
   * **后端 `sip_draft_service.required_gaps` 用的是同一条规则**（导出时拦）。
   */
  requiredWhen?: 'proj';
}

/**
 * 列顺序 = 数组顺序。**`tol` 必须在 `nominal` 之后**（先标准值、再公差）。
 *
 * ⚠️ 默认全开时的宽度合计 862px：实测容器 1280 档 1231px 仍差 19px，
 *    1440 档 1391px 富余 505px ⇒ 1440 及以上**不再横向滚动**。
 */
export const SIP_COLS: SipColDef[] = [
  { key: 'no',      label: '序号',      w: 46,  defaultOn: true,  optional: false,
    hint: '图纸气泡号（与图纸、测量报告同号）。留空 = 保存时按行序自动补' },
  { key: 'view',    label: '视图',      w: 52,  defaultOn: false, optional: true,
    hint: '视图号（0 = 主视图）。同一份里绝大多数行都是 0，所以默认收起' },
  { key: 'kind',    label: '类别',      w: 92,  defaultOn: false, optional: true,
    hint: '特征类别。界面显示中文（尺寸 / 形位公差 / 螺纹 …），内部值是英文枚举' },
  { key: 'type',    label: '类型',      w: 78,  defaultOn: false, optional: true,
    hint: '特征类型。同样显示中文；「未识别」= 这一行的公差没认出来，需要人工确认' },
  { key: 'cc_sc',   label: 'CC/SC',     w: 56,  defaultOn: true,  optional: true,
    hint: 'CC 关键特性 / SC 重要特性 / 空 = 普通。这是质量部最关心的标记，默认显示' },
  { key: 'proj',    label: '检验项目',  w: 236, defaultOn: true,  optional: false,
    hint: '写在指导书上的检验项目（图纸特征名 + 尺寸写法）' },
  { key: 'nominal', label: '标准值',    w: 82,  defaultOn: true,  optional: false,
    hint: '基本尺寸。留空 = 这一行没有标准值（会渲染成文本行，不写公式）' },
  { key: 'tol',     label: '公差 上/下', w: 92,  defaultOn: true,  optional: false,
    hint: '上下偏差合并成一格显示，但内部仍是两个独立输入框 —— 绝不合成一个字符串再拆回来（拆错就毁数据）' },
  { key: 'method',  label: '量具/方法', w: 104, defaultOn: true,  optional: false,
    hint: '现场用哪个量具或什么方法检。「检验项目」填了就是必填 —— 空着导出会被拦下',
    requiredWhen: 'proj' },
  { key: 'freq',    label: '检验频率', w: 84,  defaultOn: true,  optional: false,
    hint: '全检 / 抽检 1/5 / 首件 …（v0.53 新增）。留空时按模板口径显示，成品/出货尺寸行是 5PCS/每批；「检验项目」填了还空着，导出会被拦下',
    requiredWhen: 'proj' },
  { key: 'spec',    label: '检验标准',  w: 190, defaultOn: true,  optional: false,
    hint: '图纸上的公差带 + 基准原文（display_text）。不填就是空着，系统不替你推断' },
  { key: '_ops',    label: '',          w: 56,  defaultOn: true,  optional: false,
    hint: '上移 / 下移 / 删除' },
];

/** 默认显示的列 key 集合（**唯一出处**，别处不许再写一份字面量）。 */
export const SIP_COLS_DEFAULT_ON: string[] =
  SIP_COLS.filter((c) => c.defaultOn).map((c) => c.key);

/** 「列设置」里可开关的列（`optional === true`）。 */
export const SIP_COLS_OPTIONAL: SipColDef[] = SIP_COLS.filter((c) => c.optional);

/** 按 key 取列定义；未知 key 返回 `undefined`（调用方必须处理，不许默认兜一个）。 */
export function colDef(key: string): SipColDef | undefined {
  return SIP_COLS.find((c) => c.key === key);
}

/** 依据可见 key 集合算出 `<colgroup>` 的宽度序列。 */
export function visibleWidths(visibleKeys: string[]): number[] {
  return visibleKeys.map((k) => colDef(k)?.w ?? 0);
}

/**
 * 持久化键。⚠️ **改 `defaultOn` 就必须升版**（理由见文件头）。
 * v1 = v0.51 首次引入列显示开关时的键（此前没有任何持久化）。
 * v2 = v0.53 新增默认显示列「检验频率」—— 默认集合变了 ⇒ 换键，
 *      否则老浏览器里存的 v1 选择（没有 freq）会把新列顶掉。
 */
export const SIP_COLS_STORE_KEY = 'gdt.sip.cols.v2';

/* ================================================================
 * 持久化 hook
 *
 * ⚠️ **不用 `useEffect` 同步两份**：本地存储只在**用户点开关时**写一次，
 *   读取走 `useState` 的惰性初始化。写成「state + effect 同步」就会出现
 *   两份真相（切 tab 回来闪一下、或首次渲染用旧值再被覆盖）。
 * ================================================================ */

/** 从存储里读出可见列 key；读不到/坏了 ⇒ 用默认集合。 */
export function loadCols(): string[] {
  try {
    const raw = window.localStorage.getItem(SIP_COLS_STORE_KEY);
    if (!raw) return [...SIP_COLS_DEFAULT_ON];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...SIP_COLS_DEFAULT_ON];
    // 🔴 过滤掉**已不存在的 key**（列被删/改名后，老存储里会残留）
    const known = new Set(SIP_COLS.map((c) => c.key));
    const clean = parsed.filter((k): k is string => typeof k === 'string' && known.has(k));
    // 核心列（optional=false）**永远**在，不给关
    const must = SIP_COLS.filter((c) => !c.optional).map((c) => c.key);
    const out: string[] = [];
    for (const k of [...must, ...clean]) if (!out.includes(k)) out.push(k);
    // 按 SIP_COLS 的声明顺序排（存储里的顺序不作数）
    return SIP_COLS.map((c) => c.key).filter((k) => out.includes(k));
  } catch {
    // localStorage 不可用（隐私模式/禁用）⇒ 静默回落默认，不给用户添堵
    return [...SIP_COLS_DEFAULT_ON];
  }
}

export interface SipColsApi {
  /** 当前可见列 key（顺序 = `SIP_COLS` 声明顺序）。 */
  visible: string[];
  /** 整体替换（内部已按声明顺序归一 + 立即落盘）。 */
  set: (keys: string[]) => void;
  /** 切换一列的显隐（核心列忽略）。 */
  toggle: (key: string) => void;
  /** 是否已经偏离默认（用于在按钮上显示「恢复默认」）。 */
  dirty: boolean;
  /** 恢复默认集合。 */
  reset: () => void;
}

/** 列显示状态 + 落盘。唯一写 `localStorage` 的地方。 */
export function useSipCols(): SipColsApi {
  const [visible, setVisible] = useState<string[]>(loadCols);

  const commit = (keys: string[]) => {
    const known = new Set(SIP_COLS.map((c) => c.key));
    const must = SIP_COLS.filter((c) => !c.optional).map((c) => c.key);
    const out: string[] = [];
    for (const k of [...must, ...keys]) {
      if (known.has(k) && !out.includes(k)) out.push(k);
    }
    const ordered = SIP_COLS.map((c) => c.key).filter((k) => out.includes(k));
    setVisible(ordered);
    try {
      window.localStorage.setItem(SIP_COLS_STORE_KEY, JSON.stringify(ordered));
    } catch {
      /* 落盘失败不影响本次会话内的使用 */
    }
  };

  return {
    visible,
    set: commit,
    toggle: (key) => {
      const def = colDef(key);
      if (!def || !def.optional) return;
      commit(visible.includes(key)
        ? visible.filter((k) => k !== key)
        : [...visible, key]);
    },
    dirty: visible.length !== SIP_COLS_DEFAULT_ON.length
      || visible.some((k, i) => k !== SIP_COLS_DEFAULT_ON[i]),
    reset: () => commit([...SIP_COLS_DEFAULT_ON]),
  };
}